/* eslint-disable @typescript-eslint/no-unsafe-call */
/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { ChangeEvent, useRef, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Box,
  ModalCloseButton,
  Textarea,
  Checkbox,
  useToast,
  Text,
  useDisclosure,
  Flex,
  Tooltip,
  FormErrorMessage,
  FormControl,
} from '@chakra-ui/core';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { confirmationModalHeaderCSS } from './ConfirmationModal.styles';
import { Requisition } from '../../../../../firebase/firestore/documents/requisition';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { useSeekerContainerContext } from '../../SeekerContainerContext';
import { SelectRequisition } from '../SelectRequisition';
import { BusinessSelectType, PositionSelectType } from '../../../../administration/positionManagement/PositionType';
import { useSelectedEngagedCandidate } from '../../../MessagingViewContext';
import { ComplianceContext, PositionComplianceReminder } from '../../../../../app/components/ComplianceReminder';
import { AdditionalNoteEditor } from '../../../../../app/components/AdditionalNoteEditor';
import usePosition from '../../../sidebar/seekerList/usePosition';
import colors from '../../../../../styles/colors';
import { sanitizeOfferConditions } from '../../../../../utils/uiUtils';
import { OfferConditionsType } from '../../../../../firebase/firestore/documents/candidateAudit';
import { LanguageMap } from '../../../../../firebase/firestore/documents/user';
import { EngagedCandidate } from '../../../../../firebase/firestore/documents/candidate';

export type HireWithRequisitionModalProps = {
  isOpen: boolean;
  disabled: boolean;
  onClose: () => void;
  requisitionEnabled: boolean;
  selectedRequisitionID?: string;
  candidateFromProps?: EngagedCandidate;
  onSuccessCallback?: () => void;
};

export const HireWithRequisitionModal = ({
  isOpen,
  disabled,
  onClose,
  requisitionEnabled,
  selectedRequisitionID,
  candidateFromProps,
  onSuccessCallback,
}: HireWithRequisitionModalProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { selectedCandidate: selectedCandidateFromContext } = useSeekerContainerContext();
  const selectedEngagedCandidate = useSelectedEngagedCandidate();
  const selectedCandidate = candidateFromProps || selectedCandidateFromContext || selectedEngagedCandidate;

  const [selectedRequisition, setSelectedRequisition] = useState<Requisition | undefined>();
  const [updatedWageOfRequisition, setUpdatedWageOfRequisition] = useState<string>('0');
  /* istanbul ignore next */
  const saveOfferMadeNote = useStoreActions((actions) => actions.account.saveOfferMadeNote);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEnglishOfferEmpty, setIsEnglishOfferEmpty] = useState<boolean>(false);
  const [noteContent, setNoteContent] = useState<string>('');
  const [updatedPositionId, setUpdatedPositionId] = useState<string | undefined>(selectedCandidate?.newHireData?.positionId);
  const [updatedBusinessId, setUpdatedBusinessId] = useState<string | undefined>(selectedCandidate?.newHireData?.businessId);
  const [isWageInvalid, setIsWageInvalid] = useState<boolean>(false);
  const updateNoteContent = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContent(e.target.value);
  };
  const [sendMessageToCandidates, setSendMessageToCandidates] = useState<boolean>(true);
  const [autoDismiss, setAutoDismiss] = useState<boolean>(true);
  const [additionalNote, setAdditionalNote] = useState<OfferConditionsType | undefined>('');
  const [isPositionSalaryExceeded, setIsPositionSalaryExceeded] = useState<boolean>(false);

  const { isOpen: isOpenConformationModal, onOpen: onOpenConformationModal, onClose: onCloseConformationModal } = useDisclosure();

  /* istanbul ignore next */
  const offerComplianceCheckedDate = useStoreState((s) => s.app.user?.offerComplianceCheckedDate);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [hasUserAcknowledgedCompliance, setHasUserAcknowledgedCompliance] = useState<boolean>(false);
  const firstContentRef = useRef<HTMLDivElement | null>(null);
  /* istanbul ignore next */
  const isOfferConditionsEnabled = useStoreState((s) => s.app.accounts?.configuration?.offerConditionsEnabled);

  const positionData = usePosition(updatedPositionId || selectedCandidate?.position);

  React.useEffect(() => {
    if (!hasUserAcknowledgedCompliance && isSaveClicked && firstContentRef.current && !isEnglishOfferEmpty) {
      firstContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveClicked, hasUserAcknowledgedCompliance]);

  // istanbul ignore next
  React.useEffect(() => {
    if (selectedRequisition?.offerConditions) {
      setAdditionalNote(selectedRequisition.offerConditions);
    } else if (positionData?.offerConditions) {
      setAdditionalNote(positionData?.offerConditions);
    } else {
      setAdditionalNote(null);
    }
  }, [selectedRequisition, positionData]);

  const handleOfferMade = async () => {
    if (
      additionalNote &&
      isObject(additionalNote) &&
      isEmpty(additionalNote?.en) &&
      (!isEmpty(additionalNote?.fr) || !isEmpty(additionalNote?.es))
    ) {
      setIsEnglishOfferEmpty(true);
      return;
    }
    onCloseConformationModal();
    let commaRemovedWage = updatedWageOfRequisition.toString();
    // istanbul ignore else
    // istanbul ignore next
    if (commaRemovedWage.includes(',')) {
      commaRemovedWage = commaRemovedWage.toString().replace(',', '.');
    }
    // istanbul ignore else
    if (selectedCandidate && selectedRequisition) {
      setIsLoading(true);
      try {
        await saveOfferMadeNote({
          positionId: selectedCandidate.position,
          seekerId: selectedCandidate.seeker,
          offerMade: noteContent,
          salary: commaRemovedWage,
          salaryUnit: selectedRequisition?.wage.unit.toLowerCase(),
          customSalaryUnit: selectedRequisition?.wage.customUnit,
          requisitionId: requisitionEnabled ? selectedRequisition.requisitionId : '',
          sendMessageToCandidates,
          updatedPositionId,
          updatedBusinessId,
          autoDismiss,
          offerConditions: /* istanbul ignore next */ sanitizeOfferConditions(additionalNote as LanguageMap<string>),
        });
        setIsLoading(false);
        onSuccessCallback?.();
        onClose();
      } catch (e) {
        // istanbul ignore next
        setIsLoading(false);
        // istanbul ignore next
        toast({
          title: 'Something went Wrong!',
          description: 'Could not make offer at moment.',
          duration: 3000,
          isClosable: true,
          status: 'error',
        });
      }
    }
  };

  // istanbul ignore next
  const handleAdditionalNote = (note: LanguageMap<string> | undefined) => {
    setIsEnglishOfferEmpty(false);
    setAdditionalNote(note);
  };

  const isConfirmBtnDisabled =
    disabled ||
    !selectedRequisition ||
    updatedWageOfRequisition === '' ||
    isEnglishOfferEmpty ||
    isWageInvalid ||
    isPositionSalaryExceeded;

  return (
    <Box>
      <Modal
        id="HireConfirmationModal"
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay>
          <ModalContent>
            <ModalHeader
              css={confirmationModalHeaderCSS}
              className="modal-header"
              style={{ color: 'black' }}
              fontSize={16}
              fontWeight={700}
            >
              {t('messaging:actions.hire.sendMessageToCandidates')}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box ref={firstContentRef}>
                <AnimatePresence>
                  {isNil(offerComplianceCheckedDate) && !hasUserAcknowledgedCompliance && (
                    <motion.div
                      initial={{ opacity: 1, scale: 1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{
                        opacity: 0,
                        scale: 0.95,
                        transition: { duration: 0.3, ease: 'easeInOut' },
                      }}
                    >
                      <PositionComplianceReminder
                        isOpen={false}
                        onClose={() => ''}
                        context={ComplianceContext.OFFER}
                        onConfirm={() => setHasUserAcknowledgedCompliance(true)}
                        userDidSomeAction={isSaveClicked}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {!isNil(selectedCandidate?.earliestAvailabilityDate) && (
                  /* istanbul ignore next */
                  <Box>
                    <Text fontWeight={700}>{t('common:earliestAvailabilityDate')}</Text>
                    <Text color="gray.500" fontSize="14px">
                      {moment(selectedCandidate?.earliestAvailabilityDate).locale(i18n.language).format('ll')}
                    </Text>
                  </Box>
                )}
                <SelectRequisition
                  selectedRequisitionID={selectedRequisitionID}
                  positionId={selectedCandidate?.position || ''}
                  onRequisitionChanged={(requisition) => setSelectedRequisition(requisition)}
                  handleJobLocationChange={(businessLocationItem: BusinessSelectType | null) => {
                    setSelectedRequisition(undefined);
                    setUpdatedBusinessId(businessLocationItem?.id);
                  }}
                  handleJobChange={(jobPositionItem: PositionSelectType | null) => {
                    setSelectedRequisition(undefined);
                    setUpdatedPositionId(jobPositionItem?.id);
                  }}
                  isOpenConformationModal={isOpenConformationModal}
                  onHandleConformationModal={async (conformation: boolean) => {
                    if (conformation) {
                      await handleOfferMade();
                    } else {
                      onCloseConformationModal();
                    }
                  }}
                  updatedWageOfRequisition={updatedWageOfRequisition}
                  setUpdatedWageOfRequisition={setUpdatedWageOfRequisition}
                  setWageError={setIsWageInvalid}
                  isPositionSalaryExceeded={isPositionSalaryExceeded}
                  setIsPositionSalaryExceeded={setIsPositionSalaryExceeded}
                  candidateFromProps={candidateFromProps}
                />

                <Box>
                  <FormControl isInvalid={isEnglishOfferEmpty} isRequired>
                    {isOfferConditionsEnabled && (
                      // istanbul ignore next
                      <AdditionalNoteEditor additionalNote={additionalNote} handleNote={handleAdditionalNote} />
                    )}

                    <FormErrorMessage>{t('jobPosition:offerConditionRequired')}</FormErrorMessage>
                  </FormControl>

                  <Box mb={3}>
                    <Text fontSize="14px" fontWeight={700} mt="12px">
                      {t('requisition:offerNotes')}
                    </Text>
                    <Textarea
                      mt="2px"
                      value={noteContent}
                      data-testid="NoteContent"
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateNoteContent(e)}
                      focusBorderColor="gray"
                      height="70px"
                      backgroundColor={colors.input.background}
                      // placeholder={t('requisition:offerNotes')}
                      placeholder={t('requisition:notesPlaceholder')}
                      _placeholder={{ fontSize: 'sm' }}
                    />
                  </Box>
                  <Flex mt={6}>
                    <Tooltip
                      label={t('messaging:actions.hire.sendMessageToCandidatesToolTip')}
                      shouldWrapChildren
                      placement="top"
                    >
                      <Checkbox
                        mr={6}
                        isDisabled={!!(updatedBusinessId || updatedPositionId)}
                        isChecked={sendMessageToCandidates}
                        data-testid="sendMessageToCandidates"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setSendMessageToCandidates(e.target.checked)}
                      >
                        {t('messaging:actions.hire.sendMessageToCandidates')}
                      </Checkbox>
                    </Tooltip>
                    <Tooltip label={t('messaging:actions.hire.autoDismissCandidatesToolTip')} shouldWrapChildren placement="top">
                      <Checkbox
                        mr={2}
                        isChecked={autoDismiss}
                        isDisabled={!sendMessageToCandidates}
                        data-testid="autoDismissCandidates"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setAutoDismiss(e.target.checked)}
                      >
                        {t('messaging:actions.hire.autoDismissCandidates')}
                      </Checkbox>
                    </Tooltip>
                  </Flex>
                </Box>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button data-testid="CancelButton" colorScheme="red" mr={3} onClick={onClose} disabled={isLoading}>
                {t('messaging:actions.hire.confirmationModal.cancelButton')}
              </Button>
              <Button
                data-testid="ConfirmButton"
                colorScheme="blue"
                onClick={async () => {
                  setIsSaveClicked(true);
                  if (hasUserAcknowledgedCompliance || !isNil(offerComplianceCheckedDate)) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    updatedBusinessId || updatedPositionId
                      ? //  istanbul ignore next
                        onOpenConformationModal()
                      : await handleOfferMade();
                  }
                }}
                isLoading={isLoading}
                disabled={isConfirmBtnDisabled}
              >
                {t('messaging:actions.hire.confirmationModal.saveButton')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </Box>
  );
};
